import { type LighterWallet, RELAY_LIGHTER_CHAIN_ID } from '@funkit/fun-relay'
import { type AttemptContext, retry } from '@lifeomic/attempt'

import type { FunLogger } from '../../src/utils/funLogger'

const LIGHTER_API_BASE = 'https://mainnet.zklighter.elliot.ai'

/**
 * Live Secure withdrawal delay, fetched from Lighter.
 *
 * The endpoint returns a single number — `seconds` — representing the current
 * expected delay between an L2 Secure withdrawal request and finality on
 * Ethereum. The value is dynamic (it tracks L2→L1 batch cadence) and can range
 * from ~1 hour to many hours, so we display the live value rather than a
 * hardcoded "60+ minutes".
 *
 * https://apidocs.lighter.xyz/reference/withdrawaldelay
 */
export async function getLighterWithdrawalDelay(): Promise<{
  seconds: number
}> {
  const response = await fetch(`${LIGHTER_API_BASE}/api/v1/withdrawalDelay`)
  if (!response.ok) {
    throw new Error(
      `Failed to fetch Lighter withdrawal delay: ${response.statusText}`,
    )
  }
  return response.json()
}

// Lighter wallet adapter, inlined from @relayprotocol/relay-lighter-wallet-adapter.
// - The relay package wraps the Lighter SDK, which we don't need — the host
//   already owns a signer.
// - That wrapper also breaks the Next.js build.

export type LighterTransferParams = {
  toAccountIndex: number
  assetIndex: number
  fromRouteType: number
  toRouteType: number
  amount: number
  usdcFee: number
  memo: string
  ethSigner?: { signMessage: (message: string) => Promise<string> }
  nonce?: number
}

export type LighterTransaction = {
  hash: string
  status: number | 'pending' | 'confirmed' | 'failed'
  block_height?: number
  message?: string
}

export type LighterSigner = {
  transfer: (
    params: LighterTransferParams,
  ) => Promise<[unknown, string, string | null]>
}

interface LighterTransferStep {
  data: {
    action?: {
      type: string
      parameters: {
        toAccountIndex: number
        assetIndex: number
        fromRouteType: number
        toRouteType: number
        amount: number
        usdcFee: number
        memo: string
      }
    }
  }
}

const LIGHTER_TX_STATUS = {
  PENDING: 0,
  QUEUED: 1,
  COMMITTED: 2,
  EXECUTED: 3,
  FAILED: 4,
  REJECTED: 5,
} as const

type LighterTxStatus =
  (typeof LIGHTER_TX_STATUS)[keyof typeof LIGHTER_TX_STATUS]

function coerceLighterStatus(
  status: LighterTransaction['status'],
): LighterTxStatus {
  if (typeof status === 'number') {
    return status as LighterTxStatus
  }
  switch (status) {
    case 'confirmed':
      return LIGHTER_TX_STATUS.EXECUTED
    case 'failed':
      return LIGHTER_TX_STATUS.FAILED
    default:
      return LIGHTER_TX_STATUS.PENDING
  }
}

class LighterTerminalError extends Error {}

export async function pollLighterTransaction(
  txHash: string,
  options: {
    pollIntervalMs: number
    maxAttempts: number
    waitFor: 'findable' | 'committed'
  },
): Promise<LighterTransaction> {
  const { pollIntervalMs, maxAttempts, waitFor } = options

  return retry(
    async () => {
      const resp = await fetch(
        `${LIGHTER_API_BASE}/api/v1/tx?by=hash&value=${txHash}`,
      )
      if (!resp.ok) {
        throw new Error(`Lighter API error ${resp.status} for tx ${txHash}`)
      }
      const tx = (await resp.json()) as Partial<LighterTransaction>

      if (!tx.hash) {
        throw new Error(`Lighter tx ${txHash} not found yet`)
      }

      const status = coerceLighterStatus(tx.status ?? LIGHTER_TX_STATUS.PENDING)
      if (
        status === LIGHTER_TX_STATUS.FAILED ||
        status === LIGHTER_TX_STATUS.REJECTED
      ) {
        const label =
          status === LIGHTER_TX_STATUS.FAILED ? 'failed' : 'rejected'
        throw new LighterTerminalError(
          `Lighter transaction ${label}: ${tx.message ?? 'unknown error'}`,
        )
      }

      if (waitFor === 'findable') {
        return tx as LighterTransaction
      }

      if (
        status === LIGHTER_TX_STATUS.COMMITTED ||
        status === LIGHTER_TX_STATUS.EXECUTED
      ) {
        return tx as LighterTransaction
      }

      // Retry signal, thrown on every non-terminal poll — `retry` decides when
      // attempts run out, so this must not claim they already have.
      throw new Error(
        `Lighter transaction ${txHash} is '${status}', not yet '${waitFor}'`,
      )
    },
    {
      maxAttempts,
      delay: pollIntervalMs,
      factor: 1,
      jitter: false,
      handleError: (err: Error, context: AttemptContext) => {
        if (err instanceof LighterTerminalError) {
          context.abort()
        }
      },
    },
  )
}

export function createLighterAdaptedWallet({
  signerClient,
  accountIndex,
  logger,
}: {
  signerClient: LighterSigner
  accountIndex: number
  /** Injected: core has no module-scoped logger (web passes its funLogger). */
  logger: FunLogger
}): LighterWallet {
  async function handleSendTransactionStep(
    _chainId: number,
    stepItem: LighterTransferStep,
  ) {
    const action = stepItem.data?.action
    if (!action || action.type !== 'transfer') {
      throw new Error(
        `Unsupported Lighter action: ${action?.type ?? 'undefined'}`,
      )
    }
    const p = action.parameters
    logger.info('lighter:wallet:transfer', p)
    const [, txHash, error] = await signerClient.transfer({
      toAccountIndex: p.toAccountIndex,
      assetIndex: p.assetIndex,
      fromRouteType: p.fromRouteType,
      toRouteType: p.toRouteType,
      amount: p.amount,
      usdcFee: p.usdcFee,
      memo: p.memo,
    })
    if (error) {
      throw new Error(`Lighter transfer failed: ${error}`)
    }
    if (!txHash) {
      throw new Error('Lighter transfer returned no transaction hash')
    }
    logger.info('lighter:wallet:transfer:done', { txHash })
    return txHash
  }

  return {
    vmType: 'lvm',
    getChainId: async () => Number(RELAY_LIGHTER_CHAIN_ID),
    address: async () => accountIndex.toString(),
    handleSignMessageStep: async () => {
      throw new Error('Message signing not implemented for Lighter')
    },
    handleSendTransactionStep,
    handleConfirmTransactionStep: async (txHash: string) => {
      const tx = await pollLighterTransaction(txHash, {
        pollIntervalMs: 2_000,
        maxAttempts: 60,
        waitFor: 'findable',
      })
      return {
        txHash: tx.hash,
        blockHeight: tx.block_height ?? 0,
        status: tx.status,
      }
    },
    // Lighter is single-chain — no switching needed.
    switchChain: async () => undefined,
  } as unknown as LighterWallet
}
